import { useState, useEffect, useCallback, useRef } from 'react';

export type Panel = 'products' | 'portfolio' | 'landing' | 'blog';

export const PANELS: Panel[] = ['products', 'portfolio', 'landing', 'blog'];

export function panelIndex(panel: Panel): number {
  return PANELS.indexOf(panel);
}

interface UseHorizontalSlideOptions {
  initialPanel?: Panel;
  onPanelChange?: (panel: Panel) => void;
}

export function useHorizontalSlide({ initialPanel = 'landing', onPanelChange }: UseHorizontalSlideOptions = {}) {
  const [activePanel, setActivePanel] = useState<Panel>(initialPanel);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const transitionTimer = useRef<ReturnType<typeof setTimeout>>();

  const slideTo = useCallback(
    (panel: Panel) => {
      if (isTransitioning || activePanel === panel) return;
      setIsTransitioning(true);
      setActivePanel(panel);
      onPanelChange?.(panel);
      transitionTimer.current = setTimeout(() => setIsTransitioning(false), 700);
    },
    [isTransitioning, activePanel, onPanelChange]
  );

  const slideLeft = useCallback(() => {
    const idx = panelIndex(activePanel);
    if (idx > 0) slideTo(PANELS[idx - 1]);
  }, [activePanel, slideTo]);

  const slideRight = useCallback(() => {
    const idx = panelIndex(activePanel);
    if (idx < PANELS.length - 1) slideTo(PANELS[idx + 1]);
  }, [activePanel, slideTo]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't intercept if user is typing in an input
      const tag = (e.target as HTMLElement).tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      if ((e.target as HTMLElement).isContentEditable) return;

      if (e.key === 'ArrowRight') {
        e.preventDefault();
        slideRight();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        slideLeft();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [slideRight, slideLeft]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (transitionTimer.current) clearTimeout(transitionTimer.current);
    };
  }, []);

  return {
    activePanel,
    isTransitioning,
    slideLeft,
    slideRight,
    slideTo,
  };
}
